import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle, Download, ShieldCheck, Phone, Mail, Loader2 } from "lucide-react";
import ProposalPreview from "@/admin/ProposalPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProposalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef(null);

  const fetchProposal = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/proposal/${token}`);
      setProposal(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Proposal not found or link expired");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProposal(); }, [fetchProposal]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/proposal/${token}/accept`);
      toast.success("Proposal accepted! Our team has been notified.");
      setShowAcceptConfirm(false);
      // Re-fetch to reflect updated status
      await fetchProposal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept proposal");
    } finally { setAccepting(false); }
  };

  const handleDownloadPDF = async () => {
    const el = document.getElementById("proposal-preview");
    if (!el) return;
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 0,
        filename: `${proposal?.tracking_id || "proposal"}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      }).from(el).save();
    } catch (e) {
      toast.error("PDF export failed");
    } finally { setExporting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-stone-500 flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading proposal…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 p-4">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-8 max-w-md text-center" data-testid="public-proposal-error">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-lg font-bold text-stone-800 mb-1">Link not available</h1>
          <p className="text-sm text-stone-500">{error}</p>
          <p className="text-xs text-stone-400 mt-4">
            If you believe this is a mistake, please contact us at{" "}
            <a href="tel:478.418.2629" className="text-forest-700 hover:underline">478.418.2629</a>.
          </p>
        </div>
      </div>
    );
  }

  // Build a "form-like" object so we can reuse ProposalPreview with no changes
  const form = {
    title: proposal.title || "",
    description: proposal.description || "",
    notes: "",
    items: proposal.items || [],
    property_address: proposal.property_address || "",
    project_description: proposal.project_description || "",
    proposal_services: proposal.proposal_services || [],
    company_name: proposal.company_name || "",
    timeline: proposal.timeline || "",
    payment_terms: proposal.payment_terms || "",
    tracking_id: proposal.tracking_id || "",
    prepared_by_name: proposal.prepared_by_name || "Felipe Lima",
    prepared_by_phone: proposal.prepared_by_phone || "478.418.2629",
  };

  const isApproved = proposal.status === "approved";
  const canAccept = proposal.status === "draft" || proposal.status === "sent";

  return (
    <div className="min-h-screen bg-stone-100 pb-24">
      <Toaster position="top-right" />

      {/* Top action bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/gow-brand.png" alt="GOW" className="h-10 w-auto" />
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Proposal</p>
              <p className="text-sm font-bold text-stone-800">{proposal.tracking_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={exporting}
              data-testid="public-download-pdf"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-stone-300 text-stone-700 hover:bg-stone-50"
            >
              <Download className="w-4 h-4" /> {exporting ? "Generating…" : "Download PDF"}
            </button>
            {isApproved ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white">
                <CheckCircle className="w-4 h-4" /> Accepted
              </span>
            ) : canAccept && (
              <button
                onClick={() => setShowAcceptConfirm(true)}
                data-testid="public-accept-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-forest-600 hover:bg-forest-700 text-white shadow"
              >
                <CheckCircle className="w-4 h-4" /> Accept Proposal
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Approved banner */}
      {isApproved && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 text-green-800 text-sm">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>This proposal has been <strong>accepted</strong>. Our team will reach out shortly to schedule the service.</span>
          </div>
        </div>
      )}

      {/* Proposal preview */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div ref={previewRef} data-testid="public-proposal-preview">
          <ProposalPreview form={form} clientName={proposal.client_name} />
        </div>

        {/* Footer info */}
        <div className="mt-6 bg-white rounded-xl border border-stone-200 p-5 text-sm text-stone-600">
          <p className="font-semibold text-stone-800 mb-2">Questions about this proposal?</p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:4784182629" className="inline-flex items-center gap-1.5 text-forest-700 hover:text-forest-800">
              <Phone className="w-4 h-4" /> 478.418.2629
            </a>
            <a href="mailto:contactus@georgiaoutdoorworx.com" className="inline-flex items-center gap-1.5 text-forest-700 hover:text-forest-800">
              <Mail className="w-4 h-4" /> contactus@georgiaoutdoorworx.com
            </a>
          </div>
        </div>
      </div>

      {/* Accept confirmation modal */}
      {showAcceptConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" onClick={() => !accepting && setShowAcceptConfirm(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="public-accept-modal">
            <div className="w-12 h-12 rounded-full bg-forest-50 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-forest-600" />
            </div>
            <h2 className="text-lg font-bold text-stone-800 text-center mb-2">Accept this proposal?</h2>
            <p className="text-sm text-stone-500 text-center mb-5">
              By accepting, you authorize Georgia Outdoor Worx to begin work as outlined in this proposal. Our team will contact you shortly to schedule the service.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAcceptConfirm(false)}
                disabled={accepting}
                className="flex-1 px-4 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={accepting}
                data-testid="public-accept-confirm"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-forest-600 hover:bg-forest-700 text-white disabled:opacity-50"
              >
                {accepting ? <><Loader2 className="w-4 h-4 animate-spin" /> Accepting…</> : <><CheckCircle className="w-4 h-4" /> Confirm Accept</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
